import { Fragment } from "preact";
import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type { CustomerBody, RuntimeCustomer, RuntimeView } from "@/api/types";
import { PanelHelp } from "@/components/PanelHelp";
import { ReasonCard } from "@/components/ReasonCard";
import { RuntimeNotice } from "@/components/RuntimeNotice";
import { Staleness } from "@/components/Staleness";
import { type RuntimeChangeState, useRuntimeChange } from "@/hooks/useRuntimeChange";
import { t } from "@/i18n";

// «Betrieb › Kunden & Domains»: the partners whose pages may embed the widget,
// managed without a deploy. A customer is a name and the origins of its pages;
// the id its name gives it keys the partner report and stays fixed. What
// Terraform allows and groups is listed apart, read-only.

const WRITTEN_OUT: Record<string, string> = { ä: "ae", ö: "oe", ü: "ue", ß: "ss" };

// The backend's bound on a customer id.
const MAX_ID = 64;

/** The partner-report id a name gives: lowercase, umlauts written out, other
 * accents dropped, every run of anything but letters, digits and dots a hyphen. */
export function customerIdFrom(name: string): string {
  return name
    .normalize("NFC")
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => WRITTEN_OUT[c] ?? c)
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[^a-z0-9.]+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "")
    .slice(0, MAX_ID)
    .replace(/[-.]+$/, "");
}

/** The origins as typed: one per line or comma, trimmed, lowercase, each once. */
export function originLines(text: string): string[] {
  const lines = text.split(/[\n,]/).map((line) => line.trim().toLowerCase());
  return [...new Set(lines.filter(Boolean))];
}

/** The form's state; `origins` is the text area, one origin per line. */
export interface CustomerDraft {
  name: string;
  origins: string;
}

const EMPTY: CustomerDraft = { name: "", origins: "" };

/** The body of POST /runtime/customers/{tenant}. */
export function customerBody(
  draft: CustomerDraft,
  expectedVersion: number,
  reason: string,
): CustomerBody {
  return {
    expected_version: expectedVersion,
    reason: reason.trim(),
    name: draft.name.trim(),
    origins: originLines(draft.origins),
  };
}

export function CustomersView() {
  const change = useRuntimeChange();
  const { poll, data } = change;
  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={poll.lastFetched}
          loading={poll.loading}
          error={poll.error}
          onRefresh={poll.refetch}
        />
      </div>
      <ReasonCard change={change} />
      <RuntimeNotice change={change} saving="customers.saving" />
      {data && (
        <>
          <CustomersCard data={data} change={change} />
          <TerraformCard data={data} />
        </>
      )}
    </div>
  );
}

function OriginList({ origins }: { origins: readonly string[] }) {
  return (
    <div class="customer-origins">
      {origins.map((origin) => (
        <code key={origin}>{origin}</code>
      ))}
    </div>
  );
}

function CustomersCard({ data, change }: { data: RuntimeView; change: RuntimeChangeState }) {
  const { reason, canSave, save } = change;
  // The customer being edited, by id; null adds one.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<CustomerDraft>(EMPTY);
  const [removing, setRemoving] = useState<string | null>(null);

  const tenant = editing ?? customerIdFrom(draft.name);
  const taken =
    editing === null &&
    [...data.customers, ...data.baseline_tenants].some((c) => c.tenant === tenant);
  const ready =
    canSave &&
    tenant !== "" &&
    !taken &&
    draft.name.trim() !== "" &&
    originLines(draft.origins).length > 0;

  const close = () => {
    setEditing(null);
    setDraft(EMPTY);
  };

  const edit = (customer: RuntimeCustomer) => {
    setEditing(customer.tenant);
    setDraft({ name: customer.name, origins: customer.origins.join("\n") });
    setRemoving(null);
  };

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!ready) return;
    const body = customerBody(draft, data.version, reason);
    if (await save(() => api.setCustomer(tenant, body))) close();
  };

  const remove = async (customer: RuntimeCustomer) => {
    const body = customerBody({ name: customer.name, origins: "" }, data.version, reason);
    if (!(await save(() => api.setCustomer(customer.tenant, body)))) return;
    setRemoving(null);
    if (editing === customer.tenant) close();
  };

  const editedName = data.customers.find((c) => c.tenant === editing)?.name ?? editing ?? "";

  return (
    <section class="card">
      <h2>{t("customers.list")}</h2>
      <PanelHelp k="customers.list" />
      {data.customers.length === 0 ? (
        <div class="kpi-sub">{t("customers.empty")}</div>
      ) : (
        <table class="table">
          <thead>
            <tr>
              <th>{t("customers.col.name")}</th>
              <th>{t("customers.col.id")}</th>
              <th>{t("customers.col.origins")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.customers.map((c) => (
              <Fragment key={c.tenant}>
                <tr>
                  <td>{c.name}</td>
                  <td>
                    <code>{c.tenant}</code>
                  </td>
                  <td>
                    <OriginList origins={c.origins} />
                  </td>
                  <td>
                    <div class="customer-actions">
                      <button type="button" class="window-pill" onClick={() => edit(c)}>
                        {t("customers.edit")}
                      </button>
                      <button
                        type="button"
                        class="window-pill"
                        disabled={!canSave}
                        onClick={() => setRemoving(c.tenant)}
                      >
                        {t("customers.remove")}
                      </button>
                    </div>
                  </td>
                </tr>
                {removing === c.tenant && (
                  <tr>
                    <td colSpan={4}>
                      <div class="hl-orange">{t("customers.remove.confirm", { name: c.name })}</div>
                      <div class="customer-actions">
                        <button
                          type="button"
                          class="window-pill ops-danger-button"
                          disabled={!canSave}
                          onClick={() => void remove(c)}
                        >
                          {t("customers.remove.yes")}
                        </button>
                        <button type="button" class="window-pill" onClick={() => setRemoving(null)}>
                          {t("customers.remove.no")}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      <form class="customer-form" onSubmit={(e) => void submit(e)}>
        <h3>
          {editing ? t("customers.form.edit", { name: editedName }) : t("customers.form.add")}
        </h3>
        <PanelHelp k="customers.form" />
        <label>
          <span>{t("customers.form.name")}</span>
          <input
            type="text"
            maxLength={200}
            value={draft.name}
            onInput={(e) => setDraft({ ...draft, name: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          <span>{t("customers.form.origins")}</span>
          <textarea
            rows={4}
            spellcheck={false}
            value={draft.origins}
            onInput={(e) =>
              setDraft({ ...draft, origins: (e.target as HTMLTextAreaElement).value })
            }
          />
        </label>
        <div class="kpi-sub">{t("customers.form.originsHint")}</div>
        {tenant !== "" && <div class="kpi-sub">{t("customers.form.id", { id: tenant })}</div>}
        {tenant === "" && draft.name.trim() !== "" && (
          <div class="hl-orange">{t("customers.form.idMissing")}</div>
        )}
        {taken && <div class="hl-orange">{t("customers.form.idTaken", { id: tenant })}</div>}
        <div class="customer-actions">
          <button type="submit" class="window-pill active" disabled={!ready}>
            {t("customers.form.save")}
          </button>
          {editing && (
            <button type="button" class="window-pill" onClick={close}>
              {t("customers.form.cancel")}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function TerraformCard({ data }: { data: RuntimeView }) {
  return (
    <section class="card">
      <h2>{t("customers.terraform")}</h2>
      <PanelHelp k="customers.terraform" />
      <div class="grid grid-3">
        <div>
          <h3>{t("customers.terraform.origins")}</h3>
          <OriginList origins={data.baseline_origins} />
        </div>
        <div>
          <h3>{t("customers.terraform.partners")}</h3>
          {data.baseline_tenants.length === 0 ? (
            <div class="kpi-sub">{t("customers.terraform.noPartners")}</div>
          ) : (
            <table class="table">
              <tbody>
                {data.baseline_tenants.map((p) => (
                  <tr key={p.tenant}>
                    <td>
                      <code>{p.tenant}</code>
                    </td>
                    <td>
                      <OriginList origins={p.origins} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}
